import { useEffect } from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { env } from "@/lib/env";
import { isMockEnabled } from "@/lib/mock-flags";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useNotificationsStore, type NotifKind } from "../stores/notificationsStore";
import { listNotifications } from "../api";

const STREAM_URL = new URL("/v1/notifications/stream", env.VITE_API_BASE_URL).toString();
const RECONNECT_MS = 5000;

const mockSamples: { kind: NotifKind; title: string }[] = [
  { kind: "broker_adopted", title: "Novo corretor aderiu a um lançamento" },
  { kind: "proposal_created", title: "Nova proposta recebida" },
  { kind: "proposal_accepted", title: "Uma proposta foi aceita" },
];

function authHeader(): string {
  const token = useAuthStore.getState().accessToken;
  return token ? `Bearer ${token}` : "";
}

/**
 * Conecta ao stream SSE de notificações (`GET /v1/notifications/stream`, evento
 * `notification`) via `@microsoft/fetch-event-source` — envia o JWT no header
 * `Authorization` e reconecta sozinho. Em modo mock, emite eventos sintéticos
 * periodicamente. Deve ser chamado uma vez no AppLayout.
 */
export function useNotificationsConnection() {
  const add = useNotificationsStore((s) => s.add);
  const setConnected = useNotificationsStore((s) => s.setConnected);
  const setItems = useNotificationsStore((s) => s.setItems);

  // Carga inicial da lista (mock = fixtures; real = GET /notifications).
  useEffect(() => {
    let active = true;
    void listNotifications()
      .then((items) => active && setItems(items))
      .catch((err) => console.error("[notifications] falha ao carregar lista:", err));
    return () => {
      active = false;
    };
  }, [setItems]);

  useEffect(() => {
    // --- Modo mock: emissor sintético ---
    if (isMockEnabled("notifications")) {
      setConnected(true);
      const timer = window.setInterval(() => {
        const sample = mockSamples[Math.floor(Math.random() * mockSamples.length)];
        if (!sample) return;
        add({ id: `mock-${Date.now()}`, kind: sample.kind, title: sample.title, at: "agora" });
      }, 30_000);
      return () => {
        window.clearInterval(timer);
        setConnected(false);
      };
    }

    // --- Modo real: Server-Sent Events ---
    const controller = new AbortController();
    // Header mutável: relido a cada (re)conexão para refletir refresh de token.
    const headers: Record<string, string> = { Authorization: authHeader() };

    void fetchEventSource(STREAM_URL, {
      signal: controller.signal,
      headers,
      openWhenHidden: true,
      onopen: async (response) => {
        const contentType = response.headers.get("content-type") ?? "";
        if (response.ok && contentType.includes("text/event-stream")) {
          setConnected(true);
          return;
        }
        // Resposta inesperada (401/500/HTML) → vai para onerror e reconecta.
        throw new Error(`SSE open failed: ${response.status}`);
      },
      onmessage: (ev) => {
        // Ignora o keep-alive (`event: ping`) e mensagens sem payload.
        if (ev.event !== "notification" || !ev.data) return;
        try {
          const payload = JSON.parse(ev.data) as { kind: NotifKind; title: string; at?: string };
          add({
            id: `srv-${Date.now()}`,
            kind: payload.kind,
            title: payload.title,
            at: payload.at ?? "agora",
          });
        } catch (err) {
          console.error("[notifications] payload SSE inválido:", err);
        }
      },
      onerror: (err) => {
        console.error("[notifications] erro no stream SSE:", err);
        setConnected(false);
        headers.Authorization = authHeader();
        // Não relança: reconecta após o intervalo (backoff fixo).
        return RECONNECT_MS;
      },
      onclose: () => setConnected(false),
    });

    return () => {
      controller.abort();
      setConnected(false);
    };
  }, [add, setConnected]);
}
